import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatClock } from '@/lib/snake-market';
import { useNowMs } from '../SnakeMarkets/useNowMs';

/** Only markets closing within this window get the urgency countdown. */
const COUNTDOWN_WINDOW_MS = 60 * 60 * 1000;

export interface CountdownBadgeProps {
  /** Market/event close time (ISO). The badge self-nulls when absent, past, or further out than an hour. */
  closeTime: string | null | undefined;
  className?: string;
}

/**
 * Orange countdown notch on the lobby V2 cards (Figma 5751-141388 "Timer"):
 * a warning-tinted pill flush against the card's right edge showing
 * `HH:MM:SS` to close. Rendered only inside the last hour so the lobby
 * isn't wall-to-wall timers — it flags urgency, not schedules.
 *
 * Position: parent card must be `relative` (+ `overflow-hidden` to keep the
 * notch's square right edge clipped to the card radius).
 */
export function CountdownBadge({ closeTime, className }: CountdownBadgeProps) {
  const nowMs = useNowMs();

  if (!closeTime) return null;
  const closeMs = new Date(closeTime).getTime();
  if (Number.isNaN(closeMs)) return null;
  const remaining = closeMs - nowMs;
  if (remaining <= 0 || remaining > COUNTDOWN_WINDOW_MS) return null;

  return (
    <div
      className={cn(
        // `countdown-badge` is a styling hook: the recommended carousel pushes the
        // notch down (via `[&_.countdown-badge]:top-*`) so it clears the corner ribbon.
        'countdown-badge absolute right-0 top-[11px] flex h-6 items-center gap-1 rounded-l-pill bg-warning-soft px-2.5',
        className,
      )}
    >
      <Clock className="size-3 text-warning" />
      <span className="text-[14px] font-semibold tracking-[-0.28px] text-warning tabular-nums">
        {formatClock(closeTime, nowMs)}
      </span>
    </div>
  );
}
